import React from "react";
import { Box, Stack, Typography, Divider, Grid, Paper } from "@mui/material";
import moment from "moment";
import {
  isNational,
  routingOf,
  hasCompanyLeg,
  BRISK,
  sameCompany,
  motorPremiumExcludingPa,
  splitFromCalculation,
  chargeBasisOf,
  describeParts,
  existingTpMode,
  existingTpInsurerOf,
} from "../utils/companyRules";
import { useOnlinePolicyConfig } from "../hooks/useOnlinePolicyConfig";
import { financierView } from "../utils/financierView";
import { SECTION_TITLE_SX, NAVY, FONT } from "./formStyles";

/**
 * Helper function to format dates
 */
const formatDate = (date) => {
  if (!date) return "-";
  if (date instanceof Date) {
    return moment(date).format("DD/MM/YYYY");
  }
  if (typeof date === "string") {
    return moment(new Date(date)).format("DD/MM/YYYY");
  }
  return "-";
};

/**
 * Preview Step Component
 * Shows full details summary before creating the policy
 */
const PreviewStep = ({
  policyData,
  customerValues,
  calculationResult,
  selectedMakeModel,
  // Multiple company: the Combined Make & Model record (its TP row is the TP vehicle).
  selectedCombo = null,
  walletChecks = [],
  describeWalletCheck,
  // Edit mode: renders the Re-submit parts panel under the preview, given the
  // premium split this step already computed.
  renderEditFooter,
}) => {
  // ---------------------------------------------------------------------
  // Premium figures
  //
  // EVERY value here is run through Number() before any arithmetic. The form
  // fields arrive from Formik as STRINGS, so `finalPremium + paCoverAmount`
  // was string-concatenating instead of adding: a 10247 motor premium with a
  // "225" PA cover rendered as Rs.10247225.
  //
  // The rows below mirror the summary on the Basic Details step exactly
  // (Motor Premium + CPA with GST = Total), so the number the user approves
  // here is the same number they saw after clicking Calculate.
  // ---------------------------------------------------------------------
  const toAmount = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatAmount = (value) =>
    toAmount(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const netPremium = toAmount(calculationResult?.netPremiumAB);
  const gstAmount = toAmount(calculationResult?.tax);

  // Live form values win over the snapshot taken when the step advanced.
  const paCoverSelected = !!(customerValues?.paCover ?? policyData?.paCover);
  // National asks for a nominee on every quote, PA Cover or not — its
  // section on the Basic Details step is visible without PA Cover too.
  const previewRouting = routingOf({
    ...policyData,
    company: policyData?.company || selectedMakeModel?.company,
  });
  const isNationalPolicy = hasCompanyLeg(
    {
      ...policyData,
      company: policyData?.company || selectedMakeModel?.company,
    },
    isNational
  );
  const paCoverBase = toAmount(
    customerValues?.paCoverAmount ?? policyData?.paCoverAmount
  );
  const paCoverWithGst = paCoverSelected
    ? Number((paCoverBase * 1.18).toFixed(2))
    : 0;

  // Motor premium WITHOUT the CPA, GST included. The calculator folds the PA
  // amount into Total B, so its finalPremium already holds PA + 18% GST —
  // taken as-is, the total below counted PA twice.
  const motorPremium = toAmount(
    motorPremiumExcludingPa(calculationResult?.finalPremium, {
      paCover: paCoverSelected,
      paCoverAmount: paCoverBase,
    })
  );

  const totalPayable = Number((motorPremium + paCoverWithGst).toFixed(2));

  // Multi-company: the same premium divided by the insurer that sells each
  // part — exactly what each Company Wallet below is debited.
  const { multiProductPolicy } = useOnlinePolicyConfig();
  const premiumSplitView = splitFromCalculation(
    calculationResult,
    {
      ...policyData,
      company: policyData?.company || selectedMakeModel?.company,
      paCover: paCoverSelected,
      paCoverAmount: paCoverBase,
    },
    // An edit keeps the basis the policy was sold with (an older split was debited with GST).
    { multiProduct: multiProductPolicy, chargeBasis: chargeBasisOf(policyData) }
  );

  return (
    <Stack alignItems={"center"} sx={{ px: 0, pt: { xs: 0.5, sm: 1 }, pb: 2 }}>
      <Box sx={{ width: "100%", maxWidth: "980px" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontFamily: FONT, color: NAVY, fontSize: "18px" }}>
          Preview
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            border: "1px solid #EAECF0",
            borderRadius: "14px",
          }}
        >
          {/* 1. Customer & Address Details Section */}
          <Typography
            sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}
          >
            Customer & Address Details
          </Typography>

          {/* Customer Details */}
          <Typography
            sx={{ fontWeight: 600, mb: 1, fontSize: 14, color: "#475467", fontFamily: FONT, mt: 1 }}
          >
            Customer Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Full Name
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.fullName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Surname
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.surname || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Date of Birth
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatDate(customerValues?.dateOfBirth)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Gender
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.gender
                  ? customerValues.gender.charAt(0).toUpperCase() +
                  customerValues.gender.slice(1)
                  : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Title
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.proposerTitle || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Father's Name
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.fatherName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Mobile Number
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.mobileNumber || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Email
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.email || "-"}
              </Typography>
            </Grid>
          </Grid>

          {/* Address Details */}
          <Typography
            sx={{ fontWeight: 600, mb: 1, fontSize: 14, color: "#475467", fontFamily: FONT, mt: 2 }}
          >
            Address Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Flat/Room No
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.flatDoorNo || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Floor No
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.floorNo || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Name of Premises
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.buildingName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Block Name/No
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.blockName || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Road/Street/Lane
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.roadStreetLane || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Area and Locality
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.areaAndLocality || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                State/Union Territory
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.state || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Full Address
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {[
                  customerValues?.flatDoorNo,
                  customerValues?.floorNo,
                  customerValues?.buildingName,
                  customerValues?.blockName,
                  customerValues?.roadStreetLane,
                  customerValues?.areaAndLocality,
                  customerValues?.state,
                ]
                  .filter(Boolean)
                  .join(", ") || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Pincode
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.pincode || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Country
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.country || "-"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* 2. Basic Policy Details Section */}
          <Typography
            sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}
          >
            Basic Policy Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Products to Generate
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {[
                  policyData?.odSelected !== false &&
                    `OD — ${previewRouting.ODCompany || "-"}`,
                  policyData?.tpSelected !== false &&
                    `TP — ${previewRouting.TPCompany || "-"}`,
                ]
                  .filter(Boolean)
                  .join(", ") || "-"}
              </Typography>
            </Grid>
            {policyData?.paCover && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  PA Cover Company
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {previewRouting.PACompany
                    ? sameCompany(previewRouting.PACompany, BRISK)
                      ? "Brisk (CPA/RSA certificate)"
                      : `${previewRouting.PACompany} (with the insurer's policy)`
                    : "-"}
                </Typography>
              </Grid>
            )}
            {policyData?.companyChoice === "multiple" && selectedCombo?.displayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  Combined Make & Model
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedCombo.displayName}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                {policyData?.companyChoice === "multiple" && selectedCombo?.tpRow
                  ? `OD Make & Model (${previewRouting.ODCompany || "-"})`
                  : "Make & Model"}
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {selectedMakeModel
                  ? selectedMakeModel.make && selectedMakeModel.model
                    ? `${selectedMakeModel.make} - ${selectedMakeModel.model}`
                    : selectedMakeModel.VehicleMake &&
                      selectedMakeModel.VehicleModel
                      ? `${selectedMakeModel.VehicleMake} - ${selectedMakeModel.VehicleModel}`
                      : selectedMakeModel.label || selectedMakeModel.Value || "-"
                  : policyData?.vehicleMake && policyData?.vehicleModel
                    ? `${policyData.vehicleMake} - ${policyData.vehicleModel}`
                    : policyData?.vehicleMake || policyData?.vehicleModel || "-"}
              </Typography>
            </Grid>
            {policyData?.companyChoice === "multiple" && selectedCombo?.tpRow && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  {`TP Make & Model (${previewRouting.TPCompany || "-"})`}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {[
                    `${selectedCombo.tpRow.make} - ${selectedCombo.tpRow.model}`,
                    selectedCombo.tpRow.vehicleVariant,
                    selectedCombo.tpRow.cc ? `${selectedCombo.tpRow.cc} CC` : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                RTO City/Location
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {policyData?.rtoCityLocation || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                IDV (Insured Declared Value)
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                ₹{policyData?.idv || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Manufacturing Year
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {policyData?.manufacturingYear || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Manufacturing Month
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {policyData?.manufacturingMonth || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Purchase Date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatDate(policyData?.purchaseDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Registration Date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatDate(policyData?.registrationDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Zero Depreciation
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {policyData?.zeroDepreciation
                  ? `Yes (${policyData?.zeroDepreciationPercentage || 0})`
                  : "No"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                TPPD Restrict
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {policyData?.tppdRestrict ? "Yes" : "No"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                PA Cover
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {policyData?.paCover ? "Yes" : "No"}
              </Typography>
            </Grid>
            {(policyData?.paCover || isNationalPolicy) && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    Nominee Name
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {policyData?.nomineeName || "-"}
                  </Typography>
                </Grid>
                {!isNationalPolicy && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      Nominee Gender
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {policyData?.nomineeGender || "-"}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    Nominee Relation
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {policyData?.nomineeRelation || "-"}
                  </Typography>
                </Grid>
                {isNationalPolicy ? (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      Nominee Age
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {policyData?.nomineeAge || "-"}
                    </Typography>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      Nominee Date of Birth
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatDate(policyData?.nomineeDob)}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* 3. Vehicle Details Section */}
          <Typography
            sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}
          >
            Vehicle Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Engine Number
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.engineNumber || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Chassis Number
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {customerValues?.chassisNumber || "-"}
              </Typography>
            </Grid>
          </Grid>

          {/* 4. Financier Details - Before Premium */}
          {(customerValues?.hasFinancier || policyData?.hasFinancier) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                sx={{
                  ...SECTION_TITLE_SX,
                  mb: 1.5,
                }}
              >
                Financier Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {financierView({ ...policyData, ...customerValues, financierType: customerValues?.financierType || policyData?.financierType }).showType && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      Financier Type
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {customerValues?.financierType ||
                        policyData?.financierType ||
                        "-"}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    Financier Name
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {customerValues?.financierName ||
                      policyData?.financierName ||
                      "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    {financierView({ ...policyData, ...customerValues }).addressLabel}
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {customerValues?.financierAddress ||
                      policyData?.financierAddress ||
                      "-"}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          {/* The customer's existing TP policy (OD only; two insurers enter it once the TP part is finished) */}
          {existingTpMode({ ...policyData, ...customerValues }) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}>Third party (TP) policy</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }} data-testid="preview-existing-tp">
                {[
                  ["TP Insurer", existingTpInsurerOf({ ...policyData, ...customerValues }, existingTpMode({ ...policyData, ...customerValues }))],
                  ["TP Policy Number", customerValues?.existingTpPolicyNumber],
                  ["TP Start Date", customerValues?.existingTpStartDate ? formatDate(customerValues.existingTpStartDate) : ""],
                  ["TP End Date", customerValues?.existingTpEndDate ? formatDate(customerValues.existingTpEndDate) : ""],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: value ? undefined : "#98A2B3" }}>{value || "Not entered yet"}</Typography>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* 5. Premium Section */}
          <Typography
            sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}
          >
            Premium
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                Net Premium
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                ₹{formatAmount(netPremium)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                GST @18%
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                ₹{formatAmount(gstAmount)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  border: "1px dashed #D0D5DD",
                  borderRadius: 1.5,
                  background: "#F8FAFC",
                }}
              >
                {premiumSplitView?.isSplit ? (
                  <Stack spacing={1}>
                    {premiumSplitView.legs.map((leg) => (
                      <Stack key={leg.key} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontWeight: 700 }}>
                          {leg.company} — {leg.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          ₹{formatAmount(leg.total)}
                        </Typography>
                      </Stack>
                    ))}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 800 }}>Final Premium</Typography>
                      <Typography sx={{ fontWeight: 800 }}>
                        ₹{formatAmount(premiumSplitView.grandTotal)}
                      </Typography>
                    </Stack>
                    {premiumSplitView.byCompany.map((c) => (
                      <Stack key={c.company} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 13, color: "#475467" }}>
                          Paid to {c.company} ({describeParts(c.parts)})
                          {c.charge !== c.total ? " — excl. GST" : ""}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#475467", fontWeight: 600 }}>
                          ₹{formatAmount(c.charge)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 700 }}>
                      Motor Premium
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      ₹{formatAmount(motorPremium)}
                    </Typography>
                  </Stack>

                  {/* Only when PA Cover is actually selected — an amount left
                      over from before unticking must not inflate the total. */}
                  {paCoverSelected && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 700 }}>
                        CPA (With GST)
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }}>
                        ₹{formatAmount(paCoverWithGst)}
                      </Typography>
                    </Stack>
                  )}

                  <Divider />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 800 }}>
                      Final Premium
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>
                      ₹{formatAmount(totalPayable)}
                    </Typography>
                  </Stack>
                </Stack>
                )}
              </Box>
            </Grid>

            {/* Company Wallet balance(s) — motor premium is always checked
                against the policy's own insurer; PA Cover (when on) is an
                ADDITIONAL check against Brisk or that same insurer, per
                "PA Cover Through". The payment method (wallet vs link) is
                chosen next, in the dialog after Create is clicked — this is
                a preview of what a Wallet choice would need. */}
            {walletChecks.map((check, i) => (
              <Grid item xs={12} key={check.company || i}>
                <Box
                  sx={{
                    p: 1.5,
                    border: `1px solid ${check.sufficient ? "#A6F4C5" : "#FECDCA"}`,
                    borderRadius: 1.5,
                    background: check.sufficient ? "#F0FDF4" : "#FEF3F2",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>
                        {check.company ? `${check.company} Wallet Balance` : "Wallet Balance"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#666" }}>
                        {!check.company
                          ? "No matching Company Wallet for this insurer"
                          : describeWalletCheck(check)}
                        {!!check.company && !check.sufficient && " — top up the Company Wallet, or pay via link"}
                      </Typography>
                      {!!check.outstandingRefund && (
                        <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.5 }}>
                          Editing this policy refunds &#8377;{formatAmount(check.outstandingRefund)} already
                          charged to it before re-checking.
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: check.sufficient ? "#027A48" : "#B42318",
                          fontSize: 18,
                        }}
                      >
                        ₹{formatAmount((check.balance || 0) + (check.outstandingRefund || 0))}
                      </Typography>
                      {!!check.outstandingRefund && (
                        <Typography sx={{ fontSize: 11, color: "#98A2B3" }}>
                          (₹{formatAmount(check.balance)} now + ₹{formatAmount(check.outstandingRefund)} refund)
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {renderEditFooter && renderEditFooter({ newSplit: premiumSplitView })}
      </Box>
    </Stack>
  );
};

export default PreviewStep;
